import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { getStorages } from "../../../slices/inventory/storage";
import { warehouseStockApi } from "../../../api/inventory/warehouse-stock";
import { BuildingOffice2Icon, PrinterIcon } from "@heroicons/react/24/outline";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import { PMPagination } from "../../../components/utils/PMPagination";
import { Link } from "react-router-dom";
import { LabelPrintDialog } from "../../../components/utils/labelPrintDialog";

const WarehouseStock = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params
  const getInitialFilters = () => {
    const query = searchParams.get("query") || "";
    const storagesParam = searchParams.get("storages");
    let storages = [];
    if (storagesParam) {
      try {
        const parsed = JSON.parse(storagesParam);
        storages = Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        console.error("Error parsing storages param:", e);
        storages = [];
      }
    }
    const min_tracked_quantity = searchParams.get("min_tracked_quantity") || "";
    const max_tracked_quantity = searchParams.get("max_tracked_quantity") || "";
    return { 
      query, 
      storages,
      tracked_quantity_min: min_tracked_quantity,
      tracked_quantity_max: max_tracked_quantity,
    };
  };

  const getInitialPagination = () => {
    const page = parseInt(searchParams.get("page") || "1", 10);
    const per_page = parseInt(searchParams.get("per_page") || "50", 10);
    return { page, per_page, total: 0, total_pages: 0 };
  };

  const [filters, setFilters] = useState(getInitialFilters);
  const [stockData, setStockData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [labelPrintDialogOpen, setLabelPrintDialogOpen] = useState(false);
  const [pagination, setPagination] = useState(getInitialPagination);

  const storagesState = useSelector((store) => store.inventory_storages);
  const { data: storages, isLoading: storagesLoading } = storagesState;
  const isInitialMount = React.useRef(true);
  const isFilterChanging = React.useRef(false);

  useEffect(() => {
    dispatch(getStorages());
  }, [dispatch]);

  const fetchStockData = useCallback(async (currentFilters, currentPage, currentPerPage) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        query: currentFilters.query,
        storage_ids: currentFilters.storages,
        page: currentPage,
        per_page: currentPerPage,
      };
      
      // Add tracked quantity filters if present
      if (currentFilters.tracked_quantity_min) {
        params.min_tracked_quantity = currentFilters.tracked_quantity_min;
      }
      if (currentFilters.tracked_quantity_max) {
        params.max_tracked_quantity = currentFilters.tracked_quantity_max;
      }
      
      const response = await warehouseStockApi.getStock(params);
      setStockData(response.data || []);
      if (response.pagination) {
        setPagination((prev) => ({
          ...prev,
          page: response.pagination.page || currentPage,
          per_page: response.pagination.per_page || currentPerPage,
          total: response.pagination.total || 0,
          total_pages: response.pagination.total_pages || 0,
        }));
      }
    } catch (err) {
      console.error("Error fetching warehouse stock:", err);
      setError("Kunne ikke hente lagerbeholdning");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update URL params when state changes (but not on initial mount to avoid loop)
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    
    const params = new URLSearchParams();
    
    if (filters.query) {
      params.set("query", filters.query);
    }
    
    if (filters.storages && filters.storages.length > 0) {
      params.set("storages", JSON.stringify(filters.storages));
    }
    
    if (filters.tracked_quantity_min) {
      params.set("min_tracked_quantity", filters.tracked_quantity_min);
    }
    
    if (filters.tracked_quantity_max) {
      params.set("max_tracked_quantity", filters.tracked_quantity_max);
    }
    
    if (pagination.page > 1) {
      params.set("page", pagination.page.toString());
    }
    
    if (pagination.per_page !== 50) {
      params.set("per_page", pagination.per_page.toString());
    }
    
    setSearchParams(params, { replace: true });
  }, [filters, pagination.page, pagination.per_page, setSearchParams]);

  // Debounced search function for query changes
  const debouncedFetchStockData = useCallback(
    debounce((currentFilters, currentPage, currentPerPage) => {
      fetchStockData(currentFilters, currentPage, currentPerPage);
    }, 300),
    [fetchStockData]
  );

  // Initial load on mount
  useEffect(() => {
    fetchStockData(filters, pagination.page, pagination.per_page);
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle filter changes (with debounce for query)
  // When filters change, always fetch page 1
  useEffect(() => {
    // Skip initial mount - handled by initial load useEffect
    if (isInitialMount.current) {
      return;
    }
    
    setSelectedItems(new Set()); // Clear selections when filters change
    isFilterChanging.current = true; // Mark that filters are changing
    
    // Always fetch page 1 when filters change, not the current pagination.page
    debouncedFetchStockData(filters, 1, pagination.per_page);
    // Also reset pagination state to page 1
    setPagination((prev) => ({ ...prev, page: 1 }));

    return () => {
      debouncedFetchStockData.cancel();
      // Reset flag after debounce completes
      setTimeout(() => {
        isFilterChanging.current = false;
      }, 350);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.query, filters.storages, filters.tracked_quantity_min, filters.tracked_quantity_max]);

  // Handle page and per_page changes separately (no debounce needed)
  useEffect(() => {
    // Skip initial mount to avoid double fetch
    if (isInitialMount.current) {
      return;
    }
    
    // Skip if filters are currently changing (to avoid race condition)
    if (isFilterChanging.current) {
      return;
    }
    
    setSelectedItems(new Set()); // Clear selections when page changes
    fetchStockData(filters, pagination.page, pagination.per_page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.per_page]);

  const storageOptions = (storages || []).map((storage) => ({
    value: storage.id,
    label: storage.title || storage.storage_number || `Lager ${storage.id}`,
  }));

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    // Page reset is handled in useEffect when filters change
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (newPerPage) => {
    setPagination((prev) => ({ ...prev, per_page: newPerPage, page: 1 }));
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0";
    return parseFloat(num).toFixed(2);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = new Set(stockData.map((item) => item.product_id));
      setSelectedItems(allIds);
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (productId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedItems(newSelected);
  };

  const isAllSelected = stockData.length > 0 && selectedItems.size === stockData.length;
  const isIndeterminate = selectedItems.size > 0 && selectedItems.size < stockData.length;

  const handlePrintLabels = () => {
    if (selectedItems.size === 0) {
      return;
    }
    setLabelPrintDialogOpen(true);
  };

  const getSelectedProducts = () => {
    return stockData.filter((item) => selectedItems.has(item.product_id));
  };


  return (
    <>
      <DashboardHeader
        title="Lagerbeholdning"
        subtitle="Oversikt over produkter på lager"
      />
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <ModuleFilter
              onFiltersChange={handleFiltersChange}
              initialFilters={filters}
              searchPlaceholder="Søk etter produkt..."
              customDropdownFilters={[
                {
                  key: "storages",
                  label: "Lager",
                  icon: BuildingOffice2Icon,
                  options: storageOptions,
                },
              ]}
              customRangeFilters={[
                {
                  key: "tracked_quantity",
                  label: "Lagerført antall",
                  icon: BuildingOffice2Icon,
                  minPlaceholder: "Min",
                  maxPlaceholder: "Max",
                },
              ]}
            />
            {selectedItems.size > 0 && (
              <button
                onClick={handlePrintLabels}
                className="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PrinterIcon className="h-5 w-5 mr-2" />
                {t("Print Labels")} ({selectedItems.size})
              </button>
            )}
          </div>

          {isLoading ? (
            <ResourceLoading />
          ) : error ? (
            <ResourceError errors={[error]} />
          ) : (
            <div className="mt-6 shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        inputProps={{ "aria-label": "select all products" }}
                      />
                    </TableCell>
                    <TableCell>Produkt</TableCell>
                    <TableCell align="right">Lager</TableCell>
                    <TableCell align="right">Lagerført</TableCell>
                    <TableCell align="right">Utgående</TableCell>
                    <TableCell align="right">Innkommende</TableCell>
                    <TableCell align="right">Tilgjengelig</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <div className="py-8 text-gray-500">
                          Ingen produkter funnet
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    stockData.map((item) => (
                      <TableRow key={item.product_id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedItems.has(item.product_id)}
                            onChange={() => handleSelectItem(item.product_id)}
                            inputProps={{ "aria-label": "select product" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Link
                            to={`/dashboard/products/${item.product_id}`}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {item.product?.displayTitle ||
                              item.product?.title ||
                              item.product?.item_number ||
                              `Produkt ${item.product_id}`}
                          </Link>
                        </TableCell>
                        <TableCell align="right">
                          {item.storage?.title || "-"}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(item.tracked_quantity)}
                        </TableCell>
                        <TableCell align="right" className="text-red-600">
                          {formatNumber(item.outgoing_quantity)}
                        </TableCell>
                        <TableCell align="right" className="text-green-600">
                          {formatNumber(item.incoming_quantity)}
                        </TableCell>
                        <TableCell align="right" className="font-semibold">
                          {formatNumber(item.available_quantity)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {pagination.total > 0 && (
                <div className="mt-4">
                  <PMPagination
                    page={pagination.page}
                    perPage={pagination.per_page}
                    totalCount={pagination.total}
                    currentCount={stockData.length}
                    onPageChange={handlePageChange}
                    onPerPageChange={handlePerPageChange}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <LabelPrintDialog
        open={labelPrintDialogOpen}
        onClose={() => {
          setLabelPrintDialogOpen(false);
          setSelectedItems(new Set());
        }}
        items={getSelectedProducts()}
      />
    </>
  );
};

export default WarehouseStock;
export { WarehouseStock };

